import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useMemo } from 'react';
import { Grid, GridItem, Badge, Box } from '@chakra-ui/react';
import { DataGrid } from '../../components/data-grid/data-grid';
import { SubHeader } from '../organizations/subheader';
import { ORGANIZATION_CONSTANTS } from '../organizations/organization-constants';
import { styles } from './configuration.styles';
import theme from '../../theme';
import { useHistory } from 'react-router-dom';
import { configurationUtils } from '@codametrix/ui-common';
const { getConfigValueForServiceLine } = configurationUtils;
const FieldKeynameCellRenderer = (params)=>{
    const rowData = params.data;
    const isSystemLevel = params.context?.isSystemLevel || false;
    return /*#__PURE__*/ _jsx("div", {
        style: {
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            padding: '4px 0',
            lineHeight: '1.3'
        },
        children: /*#__PURE__*/ _jsxs(Box, {
            display: "flex",
            alignItems: "center",
            gap: 2,
            children: [
                /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("div", {
                            style: {
                                color: theme.colors.accent[200]
                            },
                            children: rowData.keyname
                        }),
                        rowData.displayName && /*#__PURE__*/ _jsx("div", {
                            style: {
                                fontSize: '0.85em',
                                color: 'gray',
                                marginTop: '4px'
                            },
                            children: rowData.displayName
                        })
                    ]
                }),
                isSystemLevel && /*#__PURE__*/ _jsx(Badge, {
                    colorScheme: "purple",
                    size: "sm",
                    children: "System"
                })
            ]
        })
    });
};
// Helper function to format values for table display (simplified for JSON)
const formatTableDisplayValue = (value)=>{
    if (!value || Array.isArray(value) && value.length === 0) {
        return /*#__PURE__*/ _jsx("span", {
            style: {
                color: 'gray'
            },
            children: "null"
        });
    }
    // For objects (JSON), show a simple indicator instead of full content
    if (typeof value === 'object' && !Array.isArray(value)) {
        return /*#__PURE__*/ _jsx("span", {
            style: {
                color: '#0066cc',
                fontStyle: 'italic',
                fontSize: '0.9em',
                backgroundColor: '#f0f8ff',
                padding: '2px 6px',
                borderRadius: '3px',
                border: '1px solid #e0e0e0'
            },
            children: "JSON Value"
        });
    }
    if (Array.isArray(value)) {
        if (value.length > 1) {
            return `[${value.join(', ')}]`;
        }
        return value[0];
    }
    return value;
};
const ConfigurationsTable = ({ configurations, serviceLines, pageSize = 100, orgCode, tenantId })=>{
    const classes = styles();
    const history = useHistory();
    const isSystemLevel = tenantId === '*';
    const handleRowClicked = (event)=>{
        const config = event.data;
        history.push(`/cmx/admin/orgs/${orgCode}/configurations/${config.id}`);
    };
    const colDefs = useMemo(()=>{
        const columns = [
            {
                headerName: 'Field (Keyname)',
                field: 'keyname',
                cellRenderer: FieldKeynameCellRenderer,
                flex: 1,
                minWidth: 150,
                autoHeight: true,
                wrapText: true,
                sort: 'asc'
            },
            {
                headerName: 'Section',
                field: 'section',
                valueGetter: (params)=>{
                    const rowData = params.data;
                    return rowData.section ?? '';
                },
                cellRenderer: (params)=>/*#__PURE__*/ _jsx("div", {
                        style: {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: '1.2',
                            paddingTop: '4px',
                            paddingBottom: '4px'
                        },
                        children: params.value || '—'
                    }),
                flex: 1,
                minWidth: 100,
                wrapText: true,
                autoHeight: true,
                cellStyle: {
                    'white-space': 'normal',
                    'word-break': 'break-word'
                }
            },
            {
                headerName: isSystemLevel ? 'System Default' : 'Tenant Default',
                valueGetter: (params)=>{
                    const rowData = params.data;
                    return getConfigValueForServiceLine(rowData.values, '*', tenantId);
                },
                cellRenderer: (params)=>{
                    const formattedValue = formatTableDisplayValue(params.value);
                    return /*#__PURE__*/ _jsx("div", {
                        style: {
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                            lineHeight: '1.2',
                            paddingTop: '4px',
                            paddingBottom: '4px'
                        },
                        children: formattedValue
                    });
                },
                flex: 1,
                minWidth: 120,
                wrapText: true,
                autoHeight: true,
                cellStyle: (params)=>{
                    const style = {
                        'white-space': 'normal',
                        'word-break': 'break-word',
                        color: 'inherit'
                    };
                    if (!params.value || Array.isArray(params.value) && params.value.length === 0) {
                        style.color = 'lightgrey';
                    }
                    return style;
                }
            }
        ];
        // Always show service line columns if serviceLines are available
        if (serviceLines && serviceLines.length > 0) {
            serviceLines.forEach((sl)=>{
                columns.push({
                    headerName: sl.name,
                    valueGetter: (params)=>{
                        const rowData = params.data;
                        return getConfigValueForServiceLine(rowData.values, sl.name, tenantId);
                    },
                    cellRenderer: (params)=>{
                        const formattedValue = formatTableDisplayValue(params.value);
                        return /*#__PURE__*/ _jsx("div", {
                            style: {
                                whiteSpace: 'normal',
                                wordBreak: 'break-word',
                                lineHeight: '1.2',
                                paddingTop: '4px',
                                paddingBottom: '4px'
                            },
                            children: formattedValue
                        });
                    },
                    flex: 1,
                    minWidth: 100,
                    wrapText: true,
                    autoHeight: true,
                    cellStyle: (params)=>{
                        const style = {
                            'white-space': 'normal',
                            'word-break': 'break-word',
                            color: 'inherit'
                        };
                        if (!params.value || Array.isArray(params.value) && params.value.length === 0) {
                            style.color = 'lightgrey';
                        }
                        return style;
                    }
                });
            });
        }
        return columns;
    }, [
        serviceLines,
        tenantId,
        isSystemLevel
    ]);
    const gridOptions = {
        suppressHorizontalScroll: true,
        onRowClicked: handleRowClicked,
        context: {
            isSystemLevel
        }
    };
    const tableTitle = isSystemLevel ? `${ORGANIZATION_CONSTANTS.CONFIGURATIONS} (System Level)` : ORGANIZATION_CONSTANTS.CONFIGURATIONS;
    return /*#__PURE__*/ _jsxs(Grid, {
        display: "flex",
        flexDirection: "column",
        flex: "1 1 0",
        minHeight: 0,
        rowGap: 5,
        children: [
            /*#__PURE__*/ _jsx(GridItem, {
                flexShrink: 0,
                children: /*#__PURE__*/ _jsx(SubHeader, {
                    className: classes.text,
                    title: tableTitle
                })
            }),
            /*#__PURE__*/ _jsx(Box, {
                flex: "1 1 0",
                minHeight: 0,
                display: "flex",
                flexDirection: "column",
                children: /*#__PURE__*/ _jsx(DataGrid, {
                    rowData: configurations,
                    columnDefs: colDefs,
                    gridOptions: gridOptions,
                    paginationPageSize: pageSize
                })
            })
        ]
    });
};
export default ConfigurationsTable;
